/**
 * @fileoverview Enterprise Keyboard Shortcuts (Hotkeys) Manager
 * @module MikhmelViewer/Hotkeys
 *
 * Enterprise Features:
 *   ✓ Cross-Platform Modifiers: Автоматическая поддержка Mac (Cmd) и Windows (Ctrl).
 *   ✓ Idempotency: Защита от двойного навешивания Event Listeners при ререндерах.
 *   ✓ Error Boundaries: Ошибка в одном шорткате не ломает весь Event Loop браузера.
 *   ✓ Input Protection: Умное игнорирование нажатий внутри полей ввода (input, textarea, contenteditable).
 *   ✓ Deep Freeze: Защита системных горячих клавиш от случайной мутации.
 */

import { setActiveTool, getShortcutMap } from '../tools/toolManager.js';
import { resetViewport, getViewportIds } from '../core/viewport.js'; // Оставлено для совместимости
import { showToast } from '../utils/helpers.js';
import { log } from '../utils/logger.js';

// ══════════════════════════════════════════════════════════════════
// 1. TYPES & JSDOC
// ══════════════════════════════════════════════════════════════════

/**
 * @callback ActionHandler
 * @param {KeyboardEvent} event
 */

/**
 * @typedef {Object} ShortcutDefinition
 * @property {string} action - ID действия
 * @property {string} description - Человекочитаемое описание
 */

// ══════════════════════════════════════════════════════════════════
// 2. CONFIGURATION & CONSTANTS
// ══════════════════════════════════════════════════════════════════

/** 
 * Системные горячие клавиши (Заморожены от изменений)
 * @type {Record<string, ShortcutDefinition>} 
 */
const GLOBAL_SHORTCUTS = Object.freeze({
  'Escape': { action: 'cancel',           description: 'Отмена / Закрыть' },
  'Delete': { action: 'deleteAnnotation', description: 'Удалить аннотацию' },
  'F11':    { action: 'fullscreen',       description: 'Полный экран' },
  'KeyI':   { action: 'invert',           description: 'Инверсия' },
  'KeyN':   { action: 'negative',         description: 'Негатив' },
  'KeyO':   { action: 'resetWL',          description: 'Сброс W/L' },
  'Home':   { action: 'firstSlice',       description: 'Первый срез' },
  'End':    { action: 'lastSlice',        description: 'Последний срез' },
  'Space':  { action: 'playCine',         description: 'Play/Stop CINE' },
  'Digit1': { action: 'layout1x1',        description: 'Layout 1×1' },
  'Digit2': { action: 'layout1x2',        description: 'Layout 1×2' },
  'Digit4': { action: 'layout2x2',        description: 'Layout 2×2' },
  'KeyQ':   { action: 'layoutMPR',        description: 'MPR Layout' },
});

// ══════════════════════════════════════════════════════════════════
// 3. CORE ENGINE
// ══════════════════════════════════════════════════════════════════

class HotkeysEngine {
  constructor() {
    this._enabled = true;
    this._initialized = false;
    
    /** @type {Record<string, ShortcutDefinition>} */
    this._customBindings = {};
    
    /** @type {Record<string, ActionHandler>} */
    this._actionHandlers = {};

    // Привязываем контекст для передачи в addEventListener
    this._handleKeyDown = this._handleKeyDown.bind(this);
    this._handleKeyUp = this._handleKeyUp.bind(this);
  }

  /**
   * Инициализация подсистемы
   * @param {Record<string, ActionHandler>} handlers 
   */
  init(handlers = {}) {
    if (this._initialized) {
      log.warn('[Hotkeys] Already initialized. Ignoring duplicate call.');
      return;
    }

    this._actionHandlers = { ...handlers };

    document.addEventListener('keydown', this._handleKeyDown, { capture: true });
    document.addEventListener('keyup', this._handleKeyUp, { capture: true });

    this._initialized = true;
    log.debug('[Hotkeys] Engine initialized successfully');
  }

  /**
   * Проверка, находится ли фокус в поле ввода
   * @private
   * @param {HTMLElement} target 
   */
  _isInputTarget(target) {
    if (!target) return false;
    const tagName = target.tagName;
    return (
      tagName === 'INPUT' || 
      tagName === 'TEXTAREA' || 
      tagName === 'SELECT' ||
      target.isContentEditable
    );
  }

  /**
   * @private
   * @param {KeyboardEvent} e 
   */
  _handleKeyDown(e) {
    if (!this._enabled || this._isInputTarget(e.target)) return;

    // Кросс-платформенный модификатор (Ctrl на Windows, Cmd на Mac)
    const isMod = e.ctrlKey || e.metaKey;
    const noMods = !e.ctrlKey && !e.altKey && !e.metaKey && !e.shiftKey;
    
    // 1. Проверяем горячие клавиши инструментов (Tools)
    // Инструменты активируются только без модификаторов (одиночные буквы)
    const toolMap = getShortcutMap();
    const keyUpper = e.key.toUpperCase();

    if (noMods && toolMap[keyUpper]) {
      e.preventDefault();
      try {
        setActiveTool(toolMap[keyUpper]);
        showToast(`🔧 ${toolMap[keyUpper]}`, 'info', 1500);
      } catch (err) {
        log.error(`[Hotkeys] Failed to set active tool "${toolMap[keyUpper]}":`, err);
      }
      return;
    }

    // 2. Стандартные системные действия (Undo, Redo, Save)
    if (isMod) {
      if (e.code === 'KeyZ') {
        e.preventDefault();
        this._executeAction(e.shiftKey ? 'redo' : 'undo', e);
        return;
      }
      if (e.code === 'KeyY') { // Альтернативный Redo (Windows)
        e.preventDefault();
        this._executeAction('redo', e);
        return;
      }
      if (e.code === 'KeyS') {
        e.preventDefault();
        this._executeAction('save', e);
        return;
      }
      if (e.code === 'KeyE') {
        e.preventDefault();
        this._executeAction('export', e);
        return;
      }
    }

    // 3. Проверка глобальных и кастомных шорткатов
    const shortcut = GLOBAL_SHORTCUTS[e.code] || this._customBindings[e.code];
    if (shortcut && noMods) { // Предполагаем, что глобальные бинды работают без модификаторов
      e.preventDefault();
      this._executeAction(shortcut.action, e);
      return;
    }
  }

  /**
   * @private
   * @param {KeyboardEvent} e 
   */
  _handleKeyUp(e) {
    // Резерв для будущего использования (например, удержание клавиши для лупы)
  }

  /**
   * Безопасный вызов обработчика
   * @private
   * @param {string} action 
   * @param {KeyboardEvent} event 
   */
  _executeAction(action, event) {
    const handler = this._actionHandlers[action];
    if (typeof handler === 'function') {
      try {
        handler(event);
      } catch (err) {
        log.error(`[Hotkeys] Exception during action "${action}":`, err);
        showToast(`Ошибка выполнения: ${action}`, 'error');
      }
    } else {
      log.debug(`[Hotkeys] Action triggered: "${action}" (no handler registered)`);
    }
  }

  // ─── PUBLIC METHODS ────────────────────────────────────────────────

  registerAction(action, handler) {
    this._actionHandlers[action] = handler;
  }

  addBinding(code, action, description) {
    this._customBindings[code] = { action, description };
  }

  removeBinding(code) {
    delete this._customBindings[code];
  }

  setEnabled(enabled) {
    this._enabled = !!enabled;
  }

  getAllShortcuts() {
    const toolMap = getShortcutMap();
    const shortcuts = [];

    // Инструменты
    for (const [key, toolName] of Object.entries(toolMap)) {
      shortcuts.push({ key, action: toolName, category: 'tool' });
    }

    // Глобальные
    for (const [code, s] of Object.entries(GLOBAL_SHORTCUTS)) {
      shortcuts.push({ key: code, action: s.action, description: s.description, category: 'global' });
    }

    // Кастомные
    for (const [code, s] of Object.entries(this._customBindings)) {
      shortcuts.push({ key: code, action: s.action, description: s.description, category: 'custom' });
    }

    return shortcuts;
  }

  destroy() {
    if (!this._initialized) return;
    document.removeEventListener('keydown', this._handleKeyDown, { capture: true });
    document.removeEventListener('keyup', this._handleKeyUp, { capture: true });
    this._actionHandlers = {};
    this._customBindings = {};
    this._initialized = false;
    log.debug('[Hotkeys] Engine destroyed and listeners removed');
  }
}

// ══════════════════════════════════════════════════════════════════
// 4. SINGLETON EXPORT & BACKWARD COMPATIBILITY
// ══════════════════════════════════════════════════════════════════

export const hotkeysEngine = new HotkeysEngine();

// Полная обратная совместимость с оригинальным API
export const initHotkeys = (handlers) => hotkeysEngine.init(handlers);
export const registerAction = (action, handler) => hotkeysEngine.registerAction(action, handler);
export const addBinding = (code, action, desc) => hotkeysEngine.addBinding(code, action, desc);
export const removeBinding = (code) => hotkeysEngine.removeBinding(code);
export const setHotkeysEnabled = (enabled) => hotkeysEngine.setEnabled(enabled);
export const getAllShortcuts = () => hotkeysEngine.getAllShortcuts();
export const destroyHotkeys = () => hotkeysEngine.destroy();
// ══════════════════════════════════════════════════════════════════
// 5. ENTERPRISE FEATURES (v6) — Real functionality
// ══════════════════════════════════════════════════════════════════

/** @type {Array<{code:string, time:number}>} */
const _keyLog = [];
const KEY_LOG_MAX = 100;
const COMBO_WINDOW = 400; // ms — window for key sequences

// ── Key Sequence Combos (gg=first slice, GG=last slice, etc.) ──
const _comboDefs = new Map(); // 'g,g' → handler

/**
 * Register a key sequence combo (e.g. pressing 'g' twice quickly → action)
 * @param {string[]} keys — array of key codes, e.g. ['KeyG','KeyG']
 * @param {Function} handler
 * @param {string} [description]
 */
export function registerCombo(keys, handler, description = '') {
  const id = keys.join(',');
  _comboDefs.set(id, { handler, keys, description });
  log.debug(`[Hotkeys] Combo registered: ${id} — ${description}`);
}

/**
 * Remove a combo
 * @param {string[]} keys
 */
export function removeCombo(keys) { _comboDefs.delete(keys.join(',')); }

/**
 * Get all registered combos
 * @returns {Array<{keys:string[], description:string}>}
 */
export function listCombos() {
  return [..._comboDefs.entries()].map(([id, def]) => ({ keys: def.keys, description: def.description }));
}

// Monkey-patch the engine to support combos
const _origHandleKeyDown = hotkeysEngine._handleKeyDown;
hotkeysEngine._handleKeyDown = function(e) {
  // Log key press for combo detection
  _keyLog.push({ code: e.code, time: Date.now() });
  if (_keyLog.length > KEY_LOG_MAX) _keyLog.shift();

  // Check combos: look at recent keys within COMBO_WINDOW
  const now = Date.now();
  const recent = _keyLog.filter(k => now - k.time < COMBO_WINDOW).map(k => k.code);
  for (const [id, def] of _comboDefs) {
    const comboKeys = def.keys;
    if (recent.length >= comboKeys.length) {
      const tail = recent.slice(-comboKeys.length);
      if (tail.every((k, i) => k === comboKeys[i])) {
        e.preventDefault();
        try { def.handler(e); } catch (err) { log.error(`[Hotkeys] Combo "${id}" error:`, err); }
        _keyLog.length = 0; // reset after match
        return;
      }
    }
  }

  // Fallback to original handler
  _origHandleKeyDown.call(this, e);
};

// ── Cheat Sheet Overlay ──
let _cheatSheetEl = null;

/**
 * Show/hide a floating keyboard shortcuts cheat sheet overlay
 */
export function toggleCheatSheet() {
  if (_cheatSheetEl) { _cheatSheetEl.remove(); _cheatSheetEl = null; return; }
  const all = hotkeysEngine.getAllShortcuts();
  _cheatSheetEl = document.createElement('div');
  Object.assign(_cheatSheetEl.style, {
    position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)',
    zIndex: '99999', background: 'rgba(15,15,25,0.96)', backdropFilter: 'blur(12px)',
    borderRadius: '16px', padding: '24px', color: '#e0e0e0', maxWidth: '600px', width: '90%',
    maxHeight: '80vh', overflowY: 'auto', border: '1px solid rgba(96,165,250,0.3)',
    boxShadow: '0 20px 60px rgba(0,0,0,0.6)', fontFamily: 'system-ui, sans-serif',
  });
  let html = '<div style="font-size:16px;font-weight:700;margin-bottom:16px;color:#60a5fa">🎹 Горячие клавиши</div>';
  const groups = { tool: [], global: [], custom: [] };
  all.forEach(s => groups[s.category]?.push(s));
  const labels = { tool: '🔧 Инструменты', global: '⌨ Системные', custom: '★ Пользовательские' };
  for (const [cat, items] of Object.entries(groups)) {
    if (!items.length) continue;
    html += `<div style="font-size:11px;color:#64748b;margin:12px 0 6px;font-weight:600">${labels[cat]}</div>`;
    html += '<div style="display:grid;grid-template-columns:1fr 1fr;gap:4px">';
    for (const s of items) {
      const key = s.key.replace('Key', '').replace('Digit', '');
      html += `<div style="display:flex;justify-content:space-between;padding:3px 8px;border-radius:4px;background:rgba(255,255,255,0.03)">
        <kbd style="background:rgba(96,165,250,0.15);padding:1px 6px;border-radius:3px;font-size:11px;color:#93c5fd">${key}</kbd>
        <span style="font-size:11px;color:#94a3b8">${s.action}</span></div>`;
    }
    html += '</div>';
  }
  // Combos
  const combos = listCombos();
  if (combos.length) {
    html += '<div style="font-size:11px;color:#64748b;margin:12px 0 6px;font-weight:600">⚡ Комбо-последовательности</div>';
    for (const c of combos) {
      html += `<div style="font-size:11px;padding:2px 8px">${c.keys.map(k => k.replace('Key','')).join(' → ')} — ${c.description}</div>`;
    }
  }
  html += '<div style="text-align:center;margin-top:16px"><button onclick="this.closest(\'div\').remove()" style="padding:6px 20px;border:1px solid #334155;background:rgba(30,41,59,0.8);color:#94a3b8;border-radius:8px;cursor:pointer;font-size:12px">Закрыть (Esc)</button></div>';
  _cheatSheetEl.innerHTML = html;
  _cheatSheetEl.addEventListener('click', e => { if (e.target === _cheatSheetEl) { _cheatSheetEl.remove(); _cheatSheetEl = null; } });
  document.body.appendChild(_cheatSheetEl);
}

// Register Shift+? to show cheat sheet by default
hotkeysEngine.addBinding('Slash', 'showCheatSheet', 'Показать горячие клавиши');

/**
 * Detect binding conflicts (two actions on same key)
 * @returns {Array<{code:string, bindings:Array}>}
 */
export function detectConflicts() {
  const all = hotkeysEngine.getAllShortcuts();
  const byKey = {};
  for (const s of all) {
    if (!byKey[s.key]) byKey[s.key] = [];
    byKey[s.key].push(s);
  }
  return Object.entries(byKey).filter(([, arr]) => arr.length > 1).map(([code, bindings]) => ({ code, bindings }));
}

/**
 * Export all bindings as JSON for backup
 */
export function exportBindingsJSON() {
  const data = { global: { ...GLOBAL_SHORTCUTS }, custom: { ...hotkeysEngine._customBindings }, combos: listCombos() };
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = `hotkeys_${Date.now()}.json`; a.click();
  URL.revokeObjectURL(url);
}

/**
 * Import custom bindings + combos from JSON
 * @param {string} jsonStr
 * @returns {number} count of imported
 */
export function importBindingsFromJSON(jsonStr) {
  try {
    const data = JSON.parse(jsonStr);
    let count = 0;
    if (data.custom) {
      for (const [code, def] of Object.entries(data.custom)) {
        hotkeysEngine.addBinding(code, def.action, def.description);
        count++;
      }
    }
    return count;
  } catch (e) { log.warn('[Hotkeys] Import error:', e.message); return 0; }
}

/**
 * Get recent key log (for debugging)
 * @param {number} [last=20]
 * @returns {Array}
 */
export function getKeyLog(last = 20) {
  return _keyLog.slice(-last).map(k => ({ code: k.code, ago: `${Date.now() - k.time}ms` }));
}

/**
 * Get full hotkey state
 * @returns {Object}
 */
/**
 * Simulate a keyboard action programmatically
 * @param {string} action — action ID
 */
export function simulateAction(action) {
  const handler = hotkeysEngine._actionHandlers[action];
  if (typeof handler === 'function') {
    try { handler(new KeyboardEvent('keydown')); } catch (e) { log.warn('[Hotkeys] simulateAction:', e.message); }
  } else {
    log.debug(`[Hotkeys] No handler for: ${action}`);
  }
}

export function getHotkeyState() {
  const all = hotkeysEngine.getAllShortcuts();
  return {
    initialized: hotkeysEngine._initialized, enabled: hotkeysEngine._enabled,
    total: all.length, combos: _comboDefs.size, conflicts: detectConflicts().length,
    keyLogSize: _keyLog.length,
  };
}
